import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Users, UserCheck, DollarSign, Share2 } from "lucide-react";
import api from "@/lib/api";
import { copyText } from "@/lib/clipboard";
import { useLang } from "@/i18n";
import { Button } from "@/components/ui/button";

export default function Affiliate() {
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [kit, setKit] = useState([]);
  useEffect(() => {
    api.get("/affiliate").then((r) => setData(r.data));
    api.get("/affiliate/kit").then((r) => setKit(r.data.posts || []));
  }, []);
  if (!data) return <div className="max-w-3xl mx-auto px-6 py-20 text-center text-stone-400 animate-pulse">{t("loading")}</div>;

  const link = `${window.location.origin}/auth?mode=signup&ref=${data.referral_code}`;
  const copy = async () => { const ok = await copyText(link); ok ? toast.success(t("copied")) : toast.error(t("err_generic")); };
  const copyPost = async (text) => { const ok = await copyText(`${text.replace("?ref=", `${window.location.origin}/auth?mode=signup&ref=`)}`); ok ? toast.success(t("copied")) : toast.error(t("err_generic")); };
  const share = async () => {
    if (navigator.share) { try { await navigator.share({ title: "BETOOL", url: link }); return; } catch (e) {} }
    copy();
  };

  const stats = [
    { icon: Users, label: t("aff_friends"), value: data.referrals, color: "text-sky-600 bg-sky-100" },
    { icon: UserCheck, label: t("aff_verified"), value: data.verified_referrals, color: "text-emerald-600 bg-emerald-100" },
    { icon: DollarSign, label: t("aff_earned"), value: `$${data.earned}`, color: "text-amber-600 bg-amber-100" },
  ];

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
      <h1 className="font-head font-black text-3xl sm:text-4xl mb-2">{t("aff_title")}</h1>
      <p className="text-stone-600 mb-8 text-lg">{t("aff_sub")}</p>

      <div className="bg-white rounded-3xl border-2 border-stone-200 p-6 mb-6">
        <p className="font-semibold text-stone-500 mb-2">{t("aff_your_link")}</p>
        <div className="flex flex-col sm:flex-row gap-3">
          <input readOnly value={link} data-testid="referral-link" className="flex-1 min-h-[52px] rounded-xl border-2 border-stone-200 px-4 text-stone-700 bg-stone-50" />
          <Button onClick={copy} data-testid="copy-link" className="rounded-full bg-primary hover:bg-[#6D28D9] min-h-[52px] font-bold"><Copy size={18} className="mr-1" />{t("copy")}</Button>
          <Button onClick={share} variant="outline" className="rounded-full border-2 min-h-[52px] font-bold"><Share2 size={18} className="mr-1" />{t("aff_share")}</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-6">
        {stats.map((s, i) => (
          <div key={i} className="bg-white rounded-3xl border-2 border-stone-200 p-6" data-testid={`aff-stat-${i}`}>
            <div className={`w-12 h-12 rounded-2xl grid place-items-center mb-3 ${s.color}`}><s.icon size={24} /></div>
            <p className="font-head font-black text-3xl">{s.value}</p>
            <p className="text-stone-500 font-medium">{s.label}</p>
          </div>
        ))}
      </div>

      <h2 className="font-head font-bold text-xl mb-1">{t("aff_kit_title")}</h2>
      <p className="text-stone-500 mb-4">{t("aff_kit_sub")}</p>
      <div className="grid sm:grid-cols-3 gap-4 mb-8" data-testid="affiliate-kit">
        {kit.map((p, i) => (
          <div key={i} className="bg-white rounded-3xl border-2 border-stone-200 p-5 flex flex-col" data-testid={`kit-post-${i}`}>
            <div className="font-head font-bold text-primary mb-2">{p.title}</div>
            <p className="text-stone-600 text-sm flex-1 whitespace-pre-wrap">{p.text.replace("?ref=", `${window.location.origin}/auth?mode=signup&ref=`)}</p>
            <Button size="sm" onClick={() => copyPost(p.text)} data-testid={`kit-copy-${i}`}
              className="rounded-full bt-gradient text-white hover:opacity-90 font-semibold mt-4"><Copy size={15} className="mr-1" />{t("aff_kit_copy")}</Button>
          </div>
        ))}
      </div>

      <h2 className="font-head font-bold text-xl mb-3">{t("aff_history")}</h2>
      <div className="bg-white rounded-3xl border-2 border-stone-200 divide-y divide-stone-100">
        {data.commissions.length === 0 && <div className="p-6 text-stone-400 text-center">—</div>}
        {data.commissions.map((c) => (
          <div key={c.id} className="flex items-center justify-between px-6 py-4">
            <span className="font-medium text-stone-600">{c.referred_email}</span>
            <span className="font-bold text-emerald-600">+${c.amount}</span>
          </div>
        ))}
      </div>
      <p className="text-sm text-stone-400 mt-4 text-center">{t("aff_note")}</p>
    </div>
  );
}
